import type { Link, Purpose } from '../types/link';

/**
 * SM_003: Install metrics (count + rate) are only meaningful for links that can
 * actually attribute an install — a referral link, or any link that carries a
 * store URL (App Download, or a Deep Link with a store fallback). Website and
 * WhatsApp links never attribute installs, so their install cards / list columns
 * / chart series are hidden instead of showing a misleading 0.
 *
 * One shared helper so the detail cards, the list columns and the
 * Clicks-over-time chart series all gate identically.
 */
export function showInstallMetrics(
  link: Pick<Link, 'kind' | 'iosAppStoreUrl' | 'androidAppStoreUrl'> | null | undefined,
): boolean {
  if (!link) return false;
  return link.kind === 'referral' || Boolean(link.iosAppStoreUrl || link.androidAppStoreUrl);
}

/**
 * SM_002: The "Link configuration" section must show only the fields relevant to
 * the link's purpose, not every field for every purpose. Common fields (name,
 * campaign, destination) always show; the rest are driven by this per-purpose map.
 */
export const PURPOSE_CONFIG_FIELDS: Record<Purpose, Array<keyof Link>> = {
  website: ['websiteUrl'],
  app_download: ['iosAppStoreUrl', 'androidAppStoreUrl', 'ipadAppStoreUrl', 'huaweiAppGalleryUrl', 'webFallbackUrl'],
  // SM_001: Deep Links also carry Play/App Store URLs (the install fallback when
  // the app isn't present) — surface them in Link Configuration, not just for the
  // App Download purpose. They're saved already; they were only missing from this list.
  deep_link: ['iosDeepLink', 'androidDeepLink', 'appScheme', 'iosUniversalLink', 'androidAppLink', 'deepLinkPath', 'androidAppStoreUrl', 'iosAppStoreUrl', 'webFallbackUrl'],
  whatsapp: [],
};
